<script setup lang="ts">
  import { computed, type HTMLAttributes } from 'vue'
  import { cn } from '../cn'

  type Props = {
    percentage?: number
    class?: HTMLAttributes['class']
    strokeWidth?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    percentage: 0,
    strokeWidth: 2
  })

  const pct = computed(() => Math.max(0, Math.min(100, props.percentage)))
  const rotation = computed(() => (pct.value / 100) * 360)

  // SVG geometry
  const cx = 12
  const cy = 14
  const r = 8

  const circumference = 2 * Math.PI * r
  const dashOffset = computed(() => circumference * (1 - pct.value / 100))

  // Hand geometry: starts pointing straight up, then rotates clockwise
  const handLength = 5
  const handX2 = cx
  const handY2 = cy - handLength
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    class="task-timer-icon"
    aria-hidden="true"
    :class="cn('size-4', props.class)"
  >
    <!-- top button -->
    <line
      x1="10"
      y1="2"
      x2="14"
      y2="2"
      class="timer-stroke"
      :stroke-width="strokeWidth"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <!-- subtle full outline -->
    <circle
      :cx="cx"
      :cy="cy"
      :r="r"
      class="timer-track"
      :stroke-width="strokeWidth"
    />

    <!-- progress outline -->
    <circle
      :cx="cx"
      :cy="cy"
      :r="r"
      class="timer-progress"
      :stroke-width="strokeWidth"
      stroke-linecap="round"
      :stroke-dasharray="circumference"
      :stroke-dashoffset="dashOffset"
      transform="rotate(-90 12 14)"
    />

    <!-- rotating hand -->
    <g :transform="`rotate(${rotation} ${cx} ${cy})`">
      <line
        :x1="cx"
        :y1="cy"
        :x2="handX2"
        :y2="handY2"
        class="timer-hand"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>

    <!-- center pin -->
    <circle :cx="cx" :cy="cy" r="1.25" class="timer-pin" />
  </svg>
</template>

<style scoped>
  .task-timer-icon {
    display: inline-block;
    vertical-align: middle;
  }

  .timer-stroke,
  .timer-hand,
  .timer-pin {
    stroke: currentColor;
    fill: currentColor;
  }

  .timer-track {
    stroke: color-mix(in srgb, currentColor 35%, transparent);
    fill: none;
  }

  .timer-progress {
    stroke: color-mix(in srgb, currentColor 80%, black);
    fill: none;
  }
</style>
